import React from 'react';
import { Card, MetricCard, Badge, SectionTitle, Spinner, ErrorMsg, fmt, curSym } from './UI';
import { useTechnical } from '../hooks/useData';

function signalBadge(s) {
  if (!s) return <Badge type="neutral">—</Badge>;
  const u = s.toUpperCase();
  if (u === 'BUY' || u === 'RIALZISTA')    return <Badge type="buy">{s}</Badge>;
  if (u === 'SELL' || u === 'RIBASSISTA')   return <Badge type="sell">{s}</Badge>;
  if (u === 'IPERCOMPRATO') return <Badge type="warning">{s}</Badge>;
  if (u === 'IPERVENDUTO')  return <Badge type="positive">{s}</Badge>;
  return <Badge type="neutral">{s}</Badge>;
}

function Row({ name, value, signal }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid #1a2030' }}>
      <span style={{ fontSize: 13, color: '#94a3b8' }}>{name}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 13, fontFamily: "'IBM Plex Mono', monospace", color: '#e2e8f0' }}>{value ?? '—'}</span>
        {signal && signalBadge(signal)}
      </div>
    </div>
  );
}

export default function TechnicalPanel({ ticker, market, currency = 'EUR' }) {
  const [timeframe, setTimeframe] = React.useState('daily');
  const { data, loading, error } = useTechnical(ticker, market, timeframe);
  const cs = curSym(currency);

  const tfBar = (
    <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
      {[['daily', 'Giornaliero'], ['weekly', 'Settimanale'], ['monthly', 'Mensile']].map(([v, label]) => (
        <button key={v} onClick={() => setTimeframe(v)} style={{
          fontSize: 12, padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
          background: timeframe === v ? '#1e3a5f' : 'transparent',
          color: timeframe === v ? '#60a5fa' : '#475569',
          border: `1px solid ${timeframe === v ? '#2563eb' : '#1e2736'}`,
          fontWeight: timeframe === v ? 600 : 400,
        }}>{label}</button>
      ))}
    </div>
  );

  if (loading) return <div>{tfBar}<Spinner /></div>;
  if (error)   return <div>{tfBar}<ErrorMsg msg={error} /></div>;
  if (!data)   return <div>{tfBar}</div>;

  const { indicators: ind, signals: sig, levels, score_technical, buy_signals, sell_signals, neutral_signals } = data;

  const rsiColor = ind.rsi_14 > 70 ? '#fbbf24' : ind.rsi_14 < 30 ? '#10b981' : '#e2e8f0';

  return (
    <div>
      {tfBar}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
        <MetricCard label="RSI (14)" value={ind.rsi_14?.toFixed(1)} sub={sig.rsi} valueColor={rsiColor} />
        <MetricCard label="MACD" value={ind.macd_hist ? (ind.macd_hist > 0 ? '+' : '') + ind.macd_hist?.toFixed(3) : '—'}
          sub={sig.macd === 'BUY' ? 'Segnale rialzista' : 'Segnale ribassista'}
          valueColor={ind.macd_hist > 0 ? '#10b981' : '#f87171'} />
        <MetricCard label="ADX (14)" value={ind.adx_14?.toFixed(1)} sub={ind.adx_14 > 25 ? 'Trend forte' : 'Trend debole'} />
        <MetricCard label="ATR (14)" value={ind.atr_14?.toFixed(3)} sub="Volatilità media" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <Card>
          <SectionTitle>Medie Mobili</SectionTitle>
          <Row name="SMA 20"  value={ind.sma_20?.toFixed(3)}  signal={sig.sma_20} />
          <Row name="SMA 50"  value={ind.sma_50?.toFixed(3)}  signal={sig.sma_50} />
          <Row name="EMA 200" value={ind.ema_200?.toFixed(3)} signal={sig.ema_200} />
          <div style={{ marginTop: 10, padding: '8px 12px', background: '#0a0c10', borderRadius: 6 }}>
            <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
              <span style={{ color: '#10b981' }}>▲ {buy_signals} buy</span>
              <span style={{ color: '#f87171' }}>▼ {sell_signals} sell</span>
              <span style={{ color: '#475569' }}>◆ {neutral_signals} neutro</span>
            </div>
          </div>
        </Card>

        <Card>
          <SectionTitle>Oscillatori</SectionTitle>
          <Row name="MACD Line"   value={ind.macd_line?.toFixed(4)}   signal={sig.macd} />
          <Row name="MACD Signal" value={ind.macd_signal?.toFixed(4)} />
          <Row name="Stocastico K" value={ind.stoch_k?.toFixed(1)}    signal={sig.stochastic} />
          <Row name="Stocastico D" value={ind.stoch_d?.toFixed(1)} />
          <Row name="Trend (ADX)"  value={ind.adx_14?.toFixed(1)}     signal={sig.trend} />
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Card>
          <SectionTitle>Bande di Bollinger</SectionTitle>
          <Row name="Banda superiore" value={`${cs} ${ind.bb_upper?.toFixed(3)}`} />
          <Row name="Banda mediana"   value={`${cs} ${ind.bb_middle?.toFixed(3)}`} />
          <Row name="Banda inferiore" value={`${cs} ${ind.bb_lower?.toFixed(3)}`} />
          {ind.bb_upper && ind.bb_lower && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#475569' }}>
              Ampiezza: {cs}{(ind.bb_upper - ind.bb_lower).toFixed(3)}
            </div>
          )}
        </Card>

        <Card>
          <SectionTitle>Supporti & Resistenze</SectionTitle>
          <Row name="Resistenza 2" value={`${cs} ${levels?.resistance_2?.toFixed(3)}`} />
          <Row name="Resistenza 1" value={`${cs} ${levels?.resistance_1?.toFixed(3)}`} />
          <Row name="Supporto 1"   value={`${cs} ${levels?.support_1?.toFixed(3)}`} />
          <Row name="Supporto 2"   value={`${cs} ${levels?.support_2?.toFixed(3)}`} />
        </Card>
      </div>
    </div>
  );
}
